import type {CreateExtArgs, EnvironmentContext} from '@wix/document-manager-core'
import type {
    FileDescriptorVideo,
    IdMapValue,
    UpdateComponentBuilderVideoParams,
    UpdateFileDescriptorResponse,
    MediaBatchContext,
    FileMediaType as FileMediaTypeType
} from '@wix/document-services-types'
import {FileMediaType} from '../../../constants/dsTypesConstants'
import {createMediaMap, getMediaInfo} from '../utils'
import {mapValues} from 'lodash'
import {fetchVideos, VideoParam} from './requests'
import {
    HandlerStatus,
    PayloadStatus,
    type EventHandlerExtApi,
    type Handler,
    type HandlerResult,
    type PayloadData
} from '../eventHandlerExtension'
import {handleAiError} from '../aiBuilderUtils'
import type {SchemaExtensionAPI} from '../../schema/schema'
import type {DataExtensionAPI} from '../../data'
import {DATA_TYPES} from '../../../constants/constants'
import {uploadSingleFileWithHandler, type UploadConfig} from '../fileUploadHandler'
import type {LoggerAPI, LoggerExtAPI} from '../../logger'
import {ERRORS, LOGS} from '../constants'
import {ReportableError} from '@wix/document-manager-utils'

const VIDEO_DATA_TYPE = 'Builder.Video'
const caller = 'generateVideo'

interface VideoData {
    name?: string
    uri?: string
    sources?: any[]
    adaptiveSources?: any[]
    poster?: any
}

const generateVideoTimeout = 240000

const addLogsToContext = (context: MediaBatchContext<VideoData>, logger: LoggerAPI) => {
    const {onSuccess, onError} = context
    context.onSuccess = (arg: any) => {
        logger.interactionEnded(caller, {tags: {caller}})
        onSuccess?.(arg)
    }
    context.onError = (err: any) => {
        const reportableError = new ReportableError({
            errorType: ERRORS.VIDEO.UPLOAD,
            message: 'Video upload failed',
            extras: {
                originalError: err
            }
        })
        logger.captureError(reportableError, {tags: {caller}})
        onError?.(err)
    }
}

const injectVideo = (createExtArgs: CreateExtArgs, idMapValue: IdMapValue, videoData: VideoData | null) => {
    const {schemaAPI} = createExtArgs.extensionAPI as SchemaExtensionAPI
    const {data} = createExtArgs.extensionAPI as DataExtensionAPI
    const {pointer, prop, path, nestedDataPath} = idMapValue ?? {}

    if (!pointer) return

    let videoDataFormatted
    if (!videoData) {
        videoDataFormatted = null
    } else {
        videoDataFormatted = schemaAPI.createItemAccordingToSchema(VIDEO_DATA_TYPE, DATA_TYPES.data, videoData)
    }
    const dataToUpdate = {[prop]: videoDataFormatted}

    data.injectData({pointer, path, nestedDataPath, partialNewData: dataToUpdate})
}

const transformVideoData = (input: any, createExtArgs: CreateExtArgs): VideoData => {
    const {schemaAPI} = createExtArgs.extensionAPI as SchemaExtensionAPI

    const adaptiveSources = input.file_output.adaptive_video.map((av: any) => ({
        format: av.format,
        uri: av.url
    }))

    const sources = input.file_output.video.map((v: any) => ({
        quality: v.quality,
        height: v.height,
        width: v.width,
        types: [
            {
                format: v.format,
                uri: v.url
            }
        ]
    }))

    const firstPoster = input.file_output.image?.[0]
    const poster = firstPoster
        ? schemaAPI.createItemAccordingToSchema('Builder.Image', DATA_TYPES.data, {
              uri: firstPoster?.url.replace('media/', ''),
              width: firstPoster.width,
              height: firstPoster.height,
              name: ''
          })
        : undefined

    return {
        uri: sources?.[0]?.types?.[0]?.uri,
        sources,
        adaptiveSources,
        poster,
        name: ''
    }
}

const createVideoUploadConfig = (
    context: MediaBatchContext<VideoData>,
    logger: LoggerAPI
): UploadConfig<VideoData | null> => ({
    extractData: (output: UpdateFileDescriptorResponse): FileDescriptorVideo | undefined => output?.file?.media?.video,
    transformData: async (videoData: FileDescriptorVideo) => {
        const mediaInfo = await getMediaInfo(context.serverFacade, videoData.id!)
        return transformVideoData(mediaInfo, context.createExtArgs)
    },
    injectItem: injectVideo,
    errorMessage: 'Video upload failed',
    timeOutInMS: 120000,
    onItemUploadStart: (url: string) => {
        logger.interactionStarted(LOGS.VIDEO.UPLOAD, {tags: {caller}, extras: {url}})
    },
    onItemUploadComplete: (url: string) => {
        logger.interactionEnded(LOGS.VIDEO.UPLOAD, {tags: {caller}, extras: {url}})
    }
})

const processVideoUrl = async (context: MediaBatchContext<VideoData>, mappedId: string, videoUrl: string) => {
    injectVideo(context.createExtArgs, context.idMap[mappedId], {uri: videoUrl, sources: [], adaptiveSources: []})
    context.createExtArgs.dal.commitTransaction()

    const {logger} = context.createExtArgs.extensionAPI as LoggerExtAPI
    const config = createVideoUploadConfig(context, logger)

    await uploadSingleFileWithHandler(context, config, mappedId, videoUrl)
}

const processPoster = (
    context: MediaBatchContext<VideoData>,
    mappedId: string,
    imageUrl: string,
    width: number,
    height: number
) => {
    const {schemaAPI} = context.createExtArgs.extensionAPI as SchemaExtensionAPI
    const imageData = {
        uri: imageUrl,
        width: Number(width),
        height: Number(height)
    }

    const poster = schemaAPI.createItemAccordingToSchema('Builder.Image', DATA_TYPES.data, imageData)
    injectVideo(context.createExtArgs, context.idMap[mappedId], {poster, sources: [], adaptiveSources: []})
    context.createExtArgs.dal.commitTransaction()
}

const handleVideoGenerationError = (
    context: MediaBatchContext<VideoData>,
    mappedId: string,
    errorMessage: string
): HandlerResult => {
    injectVideo(context.createExtArgs, context.idMap[mappedId], context.originalData[mappedId])
    context.failures.push(errorMessage)
    context.processedItems++
    context.createExtArgs.dal.commitTransaction()
    if (context.processedItems === context.totalItems) {
        context.onError?.({errors: context.failures})
    }
    return {status: HandlerStatus.FAILED, reason: errorMessage}
}

const createVideoHandler = (context: MediaBatchContext<VideoData>, mappedId: string): Handler => {
    return async (data: PayloadData): Promise<HandlerResult> => {
        const {status, output} = data ?? {}
        const {logger} = context.createExtArgs.extensionAPI as LoggerExtAPI

        switch (status) {
            case PayloadStatus.FAILED:
                const error = new ReportableError({
                    errorType: ERRORS.VIDEO.GENERATION,
                    message: 'Video generation failed'
                })
                logger.captureError(error, {tags: {caller}})
                return handleVideoGenerationError(context, mappedId, 'Video generation failed')

            case PayloadStatus.COMPLETED:
                logger.interactionEnded(LOGS.VIDEO.GENERATION, {tags: {caller}})
                addLogsToContext(context, logger)
                await processVideoUrl(context, mappedId, output.video_url)
                return {status: HandlerStatus.COMPLETED}

            case PayloadStatus.RUNNING:
                if (output?.imageUrl && output?.imageWidth && output?.imageHeight) {
                    processPoster(context, mappedId, output.imageUrl, output.imageWidth, output.imageHeight)
                }
                return {status: HandlerStatus.RUNNING}
        }
    }
}

export async function applyBuilderVideoInjection(
    {serverFacade}: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    {
        compRefWithHint,
        siteType,
        businessName,
        siteDescription,
        photoTheme,
        userRequestInfo,
        onSuccess,
        onError
    }: UpdateComponentBuilderVideoParams
) {
    const {extensionAPI} = createExtArgs
    const {logger} = extensionAPI as LoggerExtAPI
    logger.interactionStarted(caller, {tags: {caller}})

    try {
        const {compRef, hint} = compRefWithHint
        const {idMap, mediaMeasurements, originalData} = createMediaMap(extensionAPI, [compRef], 'video', caller)
        const paramsMap = mapValues(idMap, (_, mappedId): VideoParam => {
            const {width, height} = mediaMeasurements[mappedId]
            return {
                height: height.toString(),
                width: width.toString(),
                imgWidth: width.toString(),
                imgHeight: height.toString(),
                hint
            }
        })

        logger.interactionStarted(LOGS.VIDEO.GENERATION, {tags: {caller}})
        const executionIdToMappedId = await fetchVideos(serverFacade, {
            videos: paramsMap,
            siteType,
            businessTerm: businessName,
            siteDescription,
            photoTheme,
            userRequestInfo
        })

        const context: MediaBatchContext<VideoData> = {
            createExtArgs,
            idMap,
            originalData,
            onSuccess,
            onError,
            totalItems: Object.keys(executionIdToMappedId).length,
            serverFacade,
            processedItems: 0,
            failures: [],
            mediaType: FileMediaType.VIDEO as FileMediaTypeType
        }

        Object.entries(executionIdToMappedId).forEach(([executionId, mappedId]) => {
            const handler = createVideoHandler(context, mappedId)
            ;(extensionAPI as EventHandlerExtApi).eventHandler.registerExecutionIdToHandler(
                executionId,
                handler,
                caller,
                generateVideoTimeout
            )
        })
    } catch (e: any) {
        handleAiError(e, ERRORS.VIDEO.GENERATION, logger, onError, {caller})
    }
}
